import { useEffect, useState } from "react";
import type { ISpaceAuthLookupResponse } from "@/api/space/dtos/authenticator.dto";
import { useRecuperarLoginPorId, useRecuperarLoginPorNome } from "@/api/space/hooks/useSpace.authenticator";
import { deveHabilitarBuscaPorId, deveHabilitarBuscaPorNome } from "../utils/loginGrupoFixo.utils";

// --------------------------------- TYPES ---------------------------------
type UseBuscaUsuarioLoginOptions = {
	readonly username: string;
	readonly selectedUser: ISpaceAuthLookupResponse | null;
	readonly groupId: string;
	readonly enabled?: boolean;
};

// -------------------------------- HELPERS --------------------------------
function useDebouncedValue(value: string, delay: number): string {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const timeout = setTimeout(() => setDebouncedValue(value), delay);
		return () => clearTimeout(timeout);
	}, [value, delay]);

	return debouncedValue;
}

// ------------------------------- FUNCTIONS -------------------------------
export default function useBuscaUsuarioLogin({
	username,
	selectedUser,
	groupId,
	enabled = true,
}: Readonly<UseBuscaUsuarioLoginOptions>) {
	const lookupByIdEnabled = enabled && deveHabilitarBuscaPorId(username, selectedUser);
	const lookupByNameEnabled = enabled && deveHabilitarBuscaPorNome(username, selectedUser);
	const debouncedUsername = useDebouncedValue(username, 350);
	const aguardandoDebounce =
		(lookupByIdEnabled || lookupByNameEnabled) && debouncedUsername.trim() !== username.trim();

	const {
		data: optionsByName = [],
		isFetching: loadingUsersByName,
		isError: lookupByNameError,
		failureReason: lookupByNameErrorReason,
	} = useRecuperarLoginPorNome(debouncedUsername, groupId, lookupByNameEnabled);

	const {
		data: optionsByIdRaw = [],
		isFetching: loadingUsersById,
		isError: lookupByIdError,
		failureReason: lookupByIdErrorReason,
	} = useRecuperarLoginPorId(debouncedUsername, groupId, lookupByIdEnabled);

	let options: ISpaceAuthLookupResponse[] = [];
	if (!aguardandoDebounce)
		if (lookupByIdEnabled) options = optionsByIdRaw;
		else if (lookupByNameEnabled) options = optionsByName;

	return {
		options,
		debouncedUsername,
		lookupByIdEnabled,
		lookupByNameEnabled,
		isLoading: aguardandoDebounce || loadingUsersByName || loadingUsersById,
		isError: !aguardandoDebounce && (lookupByNameError || lookupByIdError),
		errorReason: lookupByNameErrorReason || lookupByIdErrorReason,
	} as const;
}
